import type {
    ChildSelector,
    PropsChildren,
} from '../components/ui-element.type';
import type { UIElement } from '../components/index';
import { resolveChildDescriptor } from '../components/ui-element.helper';
import { GenericDecorator } from './decorator.type';

export function Child(
    selector: ChildSelector
): GenericDecorator<void, ClassFieldDecoratorContext>;
export function Child(
    selector: ChildSelector,
    multiple: boolean
): GenericDecorator<void, ClassFieldDecoratorContext>;

/**
 * Defines a child element by selector or by PropsChildren.
 * @param {ChildSelector} selector - CSS selector for the child element
 * @param {boolean} [multiple] - Whether to select multiple children
 */
export function Child(
    selector: ChildSelector,
    multiple = false
): GenericDecorator<void, ClassFieldDecoratorContext> {
    return (_: void, context: ClassFieldDecoratorContext) => {
        if (context.kind === 'field') {
            context.addInitializer(function (this: unknown) {
                const instance = this as InstanceType<typeof UIElement>;
                const propertyName = String(context.name);
                const childDefinition: PropsChildren = {
                    selector,
                    multiple,
                };

                const descriptor = resolveChildDescriptor(
                    propertyName,
                    childDefinition.selector,
                    multiple
                );
                Object.defineProperty(instance, propertyName, descriptor);

                // This allows to keep backwards compatibility with legacy components,
                // which use `get static props() { ... }`
                const classConstructor =
                    instance.constructor as typeof UIElement;
                const ownDescriptor = Object.getOwnPropertyDescriptor(
                    classConstructor,
                    'props'
                );
                const isWritableDataProp =
                    ownDescriptor &&
                    !ownDescriptor.get &&
                    ownDescriptor.writable;
                if (!isWritableDataProp) {
                    const existingProps = classConstructor.props ?? {};
                    Object.defineProperty(classConstructor, 'props', {
                        value: {
                            ...existingProps,
                            children: { ...existingProps.children },
                        },
                        writable: true,
                        configurable: true,
                    });
                }
                classConstructor.props.children ??= {};
                classConstructor.props.children[propertyName] = childDefinition;
            });
        } else {
            throw new Error('@Child decorator is only applied to the property');
        }
    };
}

export function Children(
    selector: ChildSelector
): GenericDecorator<void, ClassFieldDecoratorContext>;

/**
 * Defines the children of the element by selector or by ChildSelector.
 * Same as Child, but selects multiple children.
 * @param {ChildSelector} selector - CSS selector for the child elements
 */
export function Children(
    selector: ChildSelector
): GenericDecorator<void, ClassFieldDecoratorContext> {
    return Child(selector, true);
}
